/**
 * System → Flag Cohorts: the parents a rollout flag is forced ON for before it is on for everyone.
 *
 * One `feature_flag_cohorts` row per (flag, parent). The parent's app reads its own rows through
 * `my_flag_cohorts()` when it resolves flags, so a change here reaches them at their next sign-in
 * or page load, with no deploy. Admins never need a row: `settings.json` `adminFlags` covers them.
 * The "Admins manage flag cohorts" RLS policy is what lets this page write; `bhcs flag-cohorts`
 * and the MCP `flag_cohorts_*` tools do the same through cli-api.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Trash2, UserPlus } from 'lucide-react';
import { supabase } from '../supabase';
import settings from '../../config/settings.json';
import { ROLLOUT_FLAGS } from '../../config/rolloutFlags';
import { formatDateInBusinessTZ } from '../../utils/date';

const FLAG_COHORTS_QUERY_KEY = ['admin', 'flag-cohorts'] as const;

interface CohortParent {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
}

interface CohortRow {
  flag_key: string;
  parent_id: string;
  note: string | null;
  added_by: string | null;
  created_at: string;
  parent: CohortParent | null;
}

const INPUT =
  'focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md';

function parentName(p: CohortParent | null): string {
  return p ? [p.first_name, p.last_name].filter(Boolean).join(' ') || p.email : '(deleted parent)';
}

/** What a flag does for people outside its cohort, from settings.json. */
function flagContext(flag: string): string {
  const cfg = settings as Record<string, unknown>;
  const parts = [cfg[flag] === true ? 'ON for everyone by default, so a cohort changes nothing' : 'OFF by default'];
  if ((cfg.adminFlags as Record<string, unknown> | undefined)?.[flag] === true) parts.push('ON for every admin');
  return parts.join(' · ');
}

/** Strip what would break a PostgREST `or=(…)` filter or act as a LIKE wildcard. */
function searchTerm(raw: string): string {
  return raw.replace(/[,()*%_\\"']/g, ' ').trim();
}

function useFlagCohorts() {
  return useQuery({
    queryKey: FLAG_COHORTS_QUERY_KEY,
    queryFn: async (): Promise<CohortRow[]> => {
      const { data, error } = await supabase
        .from('feature_flag_cohorts')
        .select('flag_key, parent_id, note, added_by, created_at, parent:parents(id, first_name, last_name, email)')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as CohortRow[];
    },
  });
}

/** Active parents matching the search box, and whether an answer for what is typed is still coming. */
function useParentSearch(query: string) {
  const term = searchTerm(query);
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term), 300);
    return () => clearTimeout(t);
  }, [term]);

  const { data = [], isFetching } = useQuery({
    queryKey: ['admin', 'flag-cohorts', 'parent-search', debounced],
    enabled: debounced.length >= 2,
    queryFn: async (): Promise<CohortParent[]> => {
      const { data, error } = await supabase
        .from('parents')
        .select('id, first_name, last_name, email')
        .or(`first_name.ilike.%${debounced}%,last_name.ilike.%${debounced}%,email.ilike.%${debounced}%`)
        .eq('active', true)
        .order('last_name')
        .limit(10);
      if (error) throw error;
      return (data ?? []) as CohortParent[];
    },
  });
  return { results: data, pending: debounced !== term || isFetching };
}

export function FlagCohortsTab() {
  const queryClient = useQueryClient();
  const { data: rows = [], isLoading, error: loadError } = useFlagCohorts();
  const [selected, setSelected] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [note, setNote] = useState('');
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const countByFlag = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows) counts.set(r.flag_key, (counts.get(r.flag_key) ?? 0) + 1);
    return counts;
  }, [rows]);

  // Known rollout flags, then any key that has rows but is no longer a rollout flag (a stale cohort).
  const flagOptions = useMemo(() => {
    const known = new Set(ROLLOUT_FLAGS.map(f => f.flag));
    const stale = [...countByFlag.keys()].filter(k => !known.has(k)).sort();
    return [
      ...ROLLOUT_FLAGS.map(f => ({ flag: f.flag, label: f.label })),
      ...stale.map(flag => ({ flag, label: 'not a rollout flag' })),
    ];
  }, [countByFlag]);

  const flag = selected ?? flagOptions.find(o => countByFlag.has(o.flag))?.flag ?? flagOptions[0]?.flag ?? '';
  const members = rows
    .filter(r => r.flag_key === flag)
    .sort((a, b) => parentName(a.parent).localeCompare(parentName(b.parent)));
  const memberIds = new Set(members.map(m => m.parent_id));

  const { results, pending: searching } = useParentSearch(query);
  const showResults = searchTerm(query).length >= 2;

  const refresh = () => queryClient.invalidateQueries({ queryKey: FLAG_COHORTS_QUERY_KEY });

  const addMutation = useMutation({
    mutationFn: async (parent: CohortParent) => {
      const { data: { session } } = await supabase.auth.getSession();
      const { error } = await supabase.from('feature_flag_cohorts').insert({
        flag_key: flag,
        parent_id: parent.id,
        note: note.trim() || null,
        added_by: session?.user?.email ?? null,
      });
      // 23505: someone else added them meanwhile, which is the outcome asked for.
      if (error && error.code !== '23505') throw error;
    },
    onSuccess: async () => {
      setActionError(null);
      setQuery('');
      await refresh();
    },
    onError: (e: unknown) => setActionError(e instanceof Error ? e.message : 'Could not add the parent.'),
  });

  const removeMutation = useMutation({
    mutationFn: async (parentId: string) => {
      const { error } = await supabase
        .from('feature_flag_cohorts')
        .delete()
        .eq('flag_key', flag)
        .eq('parent_id', parentId);
      if (error) throw error;
    },
    onSuccess: async () => {
      setActionError(null);
      setConfirmRemove(null);
      await refresh();
    },
    onError: (e: unknown) => setActionError(e instanceof Error ? e.message : 'Could not remove the parent.'),
  });

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div className="space-y-1">
        <p className="text-sm text-gray-600">
          Turn a rollout flag on for chosen families before it is on for everyone. A change reaches a
          parent at their next sign-in or page load. Admins get admin-first flags from{' '}
          <code className="bg-gray-100 px-1 rounded text-xs">adminFlags</code> and need no row here.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="sm:w-80">
          <label htmlFor="cohort-flag" className="block text-sm font-medium text-gray-700">Flag</label>
          <select
            id="cohort-flag"
            className={`mt-1 ${INPUT}`}
            value={flag}
            onChange={e => { setSelected(e.target.value); setConfirmRemove(null); setActionError(null); }}
          >
            {flagOptions.map(o => (
              <option key={o.flag} value={o.flag}>
                {o.flag} ({o.label}) · {countByFlag.get(o.flag) ?? 0}
              </option>
            ))}
          </select>
        </div>
        {flag && <p className="text-xs text-gray-500 sm:pb-2">{flagContext(flag)}</p>}
      </div>

      <section className="space-y-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">
          <UserPlus className="h-4 w-4 text-gray-400" aria-hidden="true" /> Add a parent
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" aria-hidden="true" />
            <input
              type="search"
              aria-label="Search parents by name or email"
              placeholder="Search parents by name or email"
              className={`${INPUT} pl-8 py-2`}
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
          <input
            type="text"
            aria-label="Note"
            placeholder="Note: why they were picked (optional)"
            className={`${INPUT} py-2`}
            value={note}
            maxLength={200}
            onChange={e => setNote(e.target.value)}
          />
        </div>
        {showResults && (
          <ul className="max-w-3xl border rounded-md divide-y bg-white" aria-label="Matching parents">
            {searching && results.length === 0 && <li className="px-3 py-2 text-sm text-gray-400">Searching…</li>}
            {!searching && results.length === 0 && <li className="px-3 py-2 text-sm text-gray-400">No active parent matches.</li>}
            {results.map(p => (
              <li key={p.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <span className="min-w-0 truncate">
                  <span className="font-medium text-gray-900">{parentName(p)}</span>
                  <span className="text-gray-500"> · {p.email}</span>
                </span>
                {memberIds.has(p.id) ? (
                  <span className="shrink-0 text-xs text-gray-400">Already in</span>
                ) : (
                  <button
                    type="button"
                    disabled={addMutation.isPending}
                    onClick={() => addMutation.mutate(p)}
                    className="shrink-0 px-3 py-1 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300"
                    aria-label={`Add ${parentName(p)}`}
                  >
                    Add
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {actionError && <p className="text-sm text-red-600" role="alert">{actionError}</p>}

      <section>
        <h3 className="text-sm font-semibold text-gray-800 mb-2">
          In the {flag} cohort ({members.length})
        </h3>
        {isLoading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : loadError ? (
          <p className="text-sm text-red-600">{loadError instanceof Error ? loadError.message : 'Could not load cohorts.'}</p>
        ) : members.length === 0 ? (
          <p className="text-sm text-gray-400">Nobody yet. Search above to add a parent.</p>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Parent</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Note</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Added</th>
                  <th />
                </tr>
              </thead>
              <tbody className="divide-y">
                {members.map(m => (
                  <tr key={m.parent_id}>
                    <td className="px-4 py-2">
                      <div className="font-medium text-gray-900">{parentName(m.parent)}</div>
                      <div className="text-xs text-gray-500">{m.parent?.email}</div>
                    </td>
                    <td className="px-4 py-2 text-gray-600">{m.note ?? '—'}</td>
                    <td className="px-4 py-2 text-gray-500 whitespace-nowrap">
                      {formatDateInBusinessTZ(m.created_at)}
                      {m.added_by && <div className="text-xs text-gray-400">{m.added_by}</div>}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {confirmRemove === m.parent_id ? (
                        <span className="inline-flex items-center gap-3 text-xs">
                          <button
                            type="button"
                            disabled={removeMutation.isPending}
                            onClick={() => removeMutation.mutate(m.parent_id)}
                            className="font-medium text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                          <button type="button" onClick={() => setConfirmRemove(null)} className="font-medium text-gray-600 hover:text-gray-800">
                            Keep
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmRemove(m.parent_id)}
                          className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-700"
                          aria-label={`Remove ${parentName(m.parent)}`}
                        >
                          <Trash2 className="h-3 w-3" /> Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
